import {
  Feature,
  IRoleWithPermissionsFieldsFragment,
  IUserPermissionFieldsFragment,
  PermissionAction,
  PermissionKey,
} from '../../utils/apollo/graphqlSchema';
import { uniqBy } from 'lodash';
import {
  IDisplayedPermissionCategory,
  IDisplayedPermissionItem,
  PermissionListType,
} from './types';

export const permissionDictionary: Record<PermissionKey, string> = {
  [PermissionKey.AdminCreate]: 'Create Role / Group',
  [PermissionKey.AdminEdit]: 'Edit User / Role / Group',
  [PermissionKey.AdminDelete]: 'Disable User / Role / Group',
  [PermissionKey.AdminRead]: 'View Admin Pages',
  [PermissionKey.AdminSettingEdit]: 'Edit Admin Settings',
  [PermissionKey.AccessAnalytics]: 'Access Kubit Analytics Schema',

  [PermissionKey.DashboardRead]: 'View Dashboard',
  [PermissionKey.DashboardCreate]: 'Create Dashboard',
  [PermissionKey.DashboardEdit]: 'Edit Dashboard',
  [PermissionKey.DashboardDelete]: 'Delete Dashboard',
  [PermissionKey.DashboardMoveTo]: 'Move/Publish Dashboard',

  [PermissionKey.DashboardFolderCreate]: 'Create Dashboard Folder',
  [PermissionKey.DashboardFolderEdit]: 'Edit Dashboard Folder',
  [PermissionKey.DashboardFolderDelete]: 'Delete Dashboard Folder',
  [PermissionKey.DashboardFolderMoveTo]: 'Move Dashboard Folder',

  [PermissionKey.DashboardScheduleRead]: 'View Dashboard Schedule',
  [PermissionKey.DashboardScheduleCreate]: 'Create Dashboard Schedule',
  [PermissionKey.DashboardScheduleEdit]: 'Edit Dashboard Schedule',
  [PermissionKey.DashboardScheduleDelete]: 'Delete Dashboard Schedule',

  [PermissionKey.AnalysisRead]: 'View Report',
  [PermissionKey.AnalysisCreate]: 'Create Report',
  [PermissionKey.AnalysisEdit]: 'Edit Report',

  [PermissionKey.AnalysisScheduleRead]: 'View Report Schedule',
  [PermissionKey.AnalysisScheduleCreate]: 'Create Report Schedule',
  [PermissionKey.AnalysisScheduleEdit]: 'Edit Report Schedule',
  [PermissionKey.AnalysisScheduleDelete]: 'Delete Report Schedule',

  [PermissionKey.MeasureRead]: 'View Measure',
  [PermissionKey.MeasureCreate]: 'Create Measure',
  [PermissionKey.MeasureEdit]: 'Edit Measure',
  [PermissionKey.MeasureDelete]: 'Delete Measure',

  [PermissionKey.FilterRead]: 'View Filter',
  [PermissionKey.FilterCreate]: 'Create Filter',
  [PermissionKey.FilterEdit]: 'Edit Filter',
  [PermissionKey.FilterDelete]: 'Delete Filter',

  [PermissionKey.VirtualEventRead]: 'View Virtual Event',
  [PermissionKey.VirtualEventCreate]: 'Create Virtual Event',
  [PermissionKey.VirtualEventEdit]: 'Edit Virtual Event',
  [PermissionKey.VirtualEventDelete]: 'Delete Virtual Event',

  [PermissionKey.EventRead]: 'View Event',
  [PermissionKey.EventEdit]: 'Edit Event',

  [PermissionKey.CohortRead]: 'View Cohort',
  [PermissionKey.CohortCreate]: 'Create Cohort',
  [PermissionKey.CohortEdit]: 'Edit Cohort',
  [PermissionKey.CohortDelete]: 'Delete Cohort',

  [PermissionKey.CohortReportRead]: 'View Cohort Report',
  [PermissionKey.CohortReportCreate]: 'Create Cohort Report',
  [PermissionKey.CohortReportEdit]: 'Edit Cohort Report',

  [PermissionKey.CohortSubjectInspect]: 'Subject Inspect',

  [PermissionKey.DataModelView]: 'View Data Model',
  [PermissionKey.DataModelEdit]: 'Edit Data Model',
  [PermissionKey.DataModelCreate]: 'Create Data Model',

  [PermissionKey.CategoryRead]: 'View Tag',
  [PermissionKey.CategoryCreate]: 'Create Tag',
  [PermissionKey.CategoryEdit]: 'Edit Tag',
  [PermissionKey.CategoryDelete]: 'Delete Tag',

  [PermissionKey.CohortBrazeExportRead]: 'View Cohort Braze Export',
  [PermissionKey.CohortBrazeExportCreate]: 'Create Cohort Braze Export',
  [PermissionKey.CohortBrazeExportEdit]: 'Edit Cohort Braze Export',
  [PermissionKey.CohortBrazeExportDelete]: 'Delete Cohort Braze Export',

  [PermissionKey.FieldRead]: 'View Field',
  [PermissionKey.FieldEdit]: 'Edit Field Tags',

  [PermissionKey.BucketDefRead]: 'View Binning',
  [PermissionKey.BucketDefCreate]: 'Create Binning',
  [PermissionKey.BucketDefEdit]: 'Edit Binning',
  [PermissionKey.BucketDefDelete]: 'Delete Binning',

  [PermissionKey.ScratchpadRead]: 'View Scratchpad',
  [PermissionKey.ScratchpadCreate]: 'Create Scratchpad',
  [PermissionKey.ScratchpadEdit]: 'Edit Scratchpad',
  [PermissionKey.ScratchpadDelete]: 'Delete Scratchpad',

  [PermissionKey.AiSummarize]: 'Summarize With AI',

  [PermissionKey.DataRefresh]: 'Refresh Data',

  [PermissionKey.ManageAllEntities]: 'Manage All Entities',
  [PermissionKey.UserInvite]: 'User Invite',
};
export const permissionActionName: Record<PermissionAction, string> = {
  [PermissionAction.Read]: 'Read',
  [PermissionAction.Create]: 'Create',
  [PermissionAction.Update]: 'Update',
  [PermissionAction.Delete]: 'Delete',
  [PermissionAction.Na]: 'Additional',
};

/**
 * This is the list of permissions that will be displayed in the UI.
 * The order of the items in this list will be respected.
 */
export const displayedPermissionList: (
  | IDisplayedPermissionItem
  | IDisplayedPermissionCategory
)[] = [
  {
    title: 'Admin',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [PermissionKey.AdminRead],
      },
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [
          PermissionKey.AdminCreate,
          PermissionKey.DataRefresh,
          PermissionKey.UserInvite,
        ],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [
          PermissionKey.AdminEdit,
          PermissionKey.AdminSettingEdit,
        ],
      },
      {
        actionKey: PermissionAction.Delete,
        permissionKeys: [PermissionKey.AdminDelete],
      },
      {
        actionKey: PermissionAction.Na,
        permissionKeys: [PermissionKey.ManageAllEntities],
      },
    ],
  },
  {
    title: 'Dashboard',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [
          PermissionKey.DashboardRead,
          PermissionKey.DashboardScheduleRead,
        ],
      },
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [
          PermissionKey.DashboardCreate,
          PermissionKey.DashboardScheduleCreate,
        ],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [
          PermissionKey.DashboardEdit,
          PermissionKey.DashboardScheduleEdit,
          PermissionKey.DashboardMoveTo,
        ],
      },
      {
        actionKey: PermissionAction.Delete,
        permissionKeys: [
          PermissionKey.DashboardDelete,
          PermissionKey.DashboardScheduleDelete,
        ],
      },
    ],
  },
  {
    title: 'Dashboard Folder',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [PermissionKey.DashboardFolderCreate],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [
          PermissionKey.DashboardFolderEdit,
          // Hide FolderMove since not implemented yet in Caprica
          // PermissionKey.DashboardFolderMoveTo,
        ],
      },
      {
        actionKey: PermissionAction.Delete,
        permissionKeys: [PermissionKey.DashboardFolderDelete],
      },
    ],
  },
  {
    title: 'Report',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [
          PermissionKey.AnalysisRead,
          PermissionKey.AnalysisScheduleRead,
        ],
      },
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [
          PermissionKey.AnalysisCreate,
          PermissionKey.AnalysisScheduleCreate,
        ],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [
          PermissionKey.AnalysisEdit,
          PermissionKey.AnalysisScheduleEdit,
        ],
      },
      {
        actionKey: PermissionAction.Delete,
        permissionKeys: [PermissionKey.AnalysisScheduleDelete],
      },
    ],
  },
  {
    title: 'Cohort',
    type: PermissionListType.Category,
    list: [
      {
        title: 'Named',
        type: PermissionListType.Items,
        items: [
          {
            actionKey: PermissionAction.Read,
            permissionKeys: [PermissionKey.CohortRead],
          },
          {
            actionKey: PermissionAction.Create,
            permissionKeys: [PermissionKey.CohortCreate],
          },
          {
            actionKey: PermissionAction.Update,
            permissionKeys: [PermissionKey.CohortEdit],
          },
          {
            actionKey: PermissionAction.Delete,
            permissionKeys: [PermissionKey.CohortDelete],
          },
        ],
      },
      {
        title: 'Report',
        type: PermissionListType.Items,
        items: [
          {
            actionKey: PermissionAction.Read,
            permissionKeys: [PermissionKey.CohortReportRead],
          },
          {
            actionKey: PermissionAction.Create,
            permissionKeys: [PermissionKey.CohortReportCreate],
          },
          {
            actionKey: PermissionAction.Update,
            permissionKeys: [PermissionKey.CohortReportEdit],
          },
        ],
      },
      {
        title: 'Subject Inspect',
        type: PermissionListType.Items,
        items: [
          {
            actionKey: PermissionAction.Create,
            permissionKeys: [PermissionKey.CohortSubjectInspect],
          },
        ],
      },
      {
        title: 'Braze Export',
        type: PermissionListType.Items,
        items: [
          {
            actionKey: PermissionAction.Read,
            permissionKeys: [PermissionKey.CohortBrazeExportRead],
          },
          {
            actionKey: PermissionAction.Create,
            permissionKeys: [PermissionKey.CohortBrazeExportCreate],
          },
          {
            actionKey: PermissionAction.Update,
            permissionKeys: [PermissionKey.CohortBrazeExportEdit],
          },
          {
            actionKey: PermissionAction.Delete,
            permissionKeys: [PermissionKey.CohortBrazeExportDelete],
          },
        ],
      },
    ],
  },
  {
    title: 'Event',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [PermissionKey.EventRead],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [PermissionKey.EventEdit],
      },
    ],
  },
  {
    title: 'Virtual Event',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [PermissionKey.VirtualEventRead],
      },
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [PermissionKey.VirtualEventCreate],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [PermissionKey.VirtualEventEdit],
      },
      {
        actionKey: PermissionAction.Delete,
        permissionKeys: [PermissionKey.VirtualEventDelete],
      },
    ],
  },
  {
    title: 'Field',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [PermissionKey.FieldRead],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [PermissionKey.FieldEdit],
      },
    ],
  },
  {
    title: 'Filter',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [PermissionKey.FilterRead],
      },
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [PermissionKey.FilterCreate],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [PermissionKey.FilterEdit],
      },
      {
        actionKey: PermissionAction.Delete,
        permissionKeys: [PermissionKey.FilterDelete],
      },
    ],
  },
  {
    title: 'Measure',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [PermissionKey.MeasureRead],
      },
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [PermissionKey.MeasureCreate],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [PermissionKey.MeasureEdit],
      },
      {
        actionKey: PermissionAction.Delete,
        permissionKeys: [PermissionKey.MeasureDelete],
      },
    ],
  },
  {
    title: 'Binning',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [PermissionKey.BucketDefRead],
      },
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [PermissionKey.BucketDefCreate],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [PermissionKey.BucketDefEdit],
      },
      {
        actionKey: PermissionAction.Delete,
        permissionKeys: [PermissionKey.BucketDefDelete],
      },
    ],
  },
  {
    title: 'Scratchpad',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [PermissionKey.ScratchpadRead],
      },
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [PermissionKey.ScratchpadCreate],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [PermissionKey.ScratchpadEdit],
      },
      {
        actionKey: PermissionAction.Delete,
        permissionKeys: [PermissionKey.ScratchpadDelete],
      },
    ],
  },
  {
    title: 'Data Model',
    type: PermissionListType.Items,
    requiredFeature: Feature.DataModeler,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [PermissionKey.DataModelView],
      },
      {
        actionKey: PermissionAction.Update,
        permissionKeys: [PermissionKey.DataModelEdit],
        requiredFeature: Feature.DataModelerEdit,
      },
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [PermissionKey.DataModelCreate],
        requiredFeature: Feature.DataModelerEdit,
      },
    ],
  },
  {
    title: 'Tags',
    type: PermissionListType.Items,
    items: [
      {
        actionKey: PermissionAction.Read,
        permissionKeys: [PermissionKey.CategoryRead],
      },
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [PermissionKey.CategoryCreate],
      },
    ],
  },
  {
    title: 'AI Access',
    type: PermissionListType.Items,
    requiredFeature: Feature.AiExplain,
    items: [
      {
        actionKey: PermissionAction.Create,
        permissionKeys: [PermissionKey.AiSummarize],
      },
    ],
  },
  {
    title: 'Kubit Analytics',
    type: PermissionListType.Items,
    requiredFeature: Feature.KubitAnalytics,
    items: [
      {
        actionKey: PermissionAction.Na,
        permissionKeys: [PermissionKey.AccessAnalytics],
      },
    ],
  },
];

export const collectAllUniqRolesFromUser = (
  user: IUserPermissionFieldsFragment,
) => {
  const rolesFromGroups = user.groups.flatMap((group) => group.roles ?? []);
  return uniqBy([...user.roles, ...rolesFromGroups], (role) => role.id);
};

export const getPermissionKeyAndSchemaIdsFromRoles = (
  roles: IRoleWithPermissionsFieldsFragment[],
) => {
  return roles.flatMap((role) => {
    return role.permissionAssignments.map((permissionAssignment) => {
      const { permission } = permissionAssignment;
      return {
        permissionKey: permission.key,
        schemaIds: permissionAssignment.schemas.map((schema) => schema.id),
      };
    });
  });
};

// return null if allSchema is allowed
export const getUniqueAvailableSchemaIdsFromRolesByPermissionKey = (
  roles: IRoleWithPermissionsFieldsFragment[],
  permissionKeys: PermissionKey[],
  allSchemaId: string,
) => {
  const availableSchemaIds = roles.flatMap((role) => {
    return role.permissionAssignments
      .filter((permissionAssignment) =>
        permissionKeys.includes(permissionAssignment.permission.key),
      )
      .flatMap((permissionAssignment) =>
        permissionAssignment.schemas.map((schema) => schema.id),
      );
  });

  const uniqueSchemaIds = [...new Set(availableSchemaIds)];
  return uniqueSchemaIds.includes(allSchemaId) ? [] : uniqueSchemaIds;
};
